/**
 * Activity log filtered to a single actor — opened from a name on the main Activity log.
 */
import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, RefreshCw, User } from 'lucide-react';
import api from '../../utils/api';
import ActivityLogTable from '../../components/ActivityLogTable';
import AuthSessionTable from '../../components/AuthSessionTable';

export default function ActorActivity() {
    const { actorId } = useParams();
    const [items, setItems] = useState([]);
    const [authItems, setAuthItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [authTotal, setAuthTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit] = useState(50);
    const [loading, setLoading] = useState(false);
    const [actorMeta, setActorMeta] = useState(null);

    const loadActorMeta = async () => {
        try {
            const { data: u } = await api.get(`/users/${actorId}`);
            setActorMeta({
                name: u.name || 'Unknown',
                role: u.role || '—',
                email: u.email,
                lastLoginAt: u.lastLoginAt,
            });
        } catch {
            /* fallback from first activity row */
        }
    };

    const load = async (p = page) => {
        setLoading(true);
        try {
            const [appRes, authRes] = await Promise.all([
                api.get('/activity', { params: { actor: actorId, excludeEntity: 'Auth', page: p, limit } }),
                api.get('/activity', { params: { actor: actorId, entity: 'Auth', page: 1, limit: 100 } }),
            ]);
            setItems(appRes.data.items);
            setTotal(appRes.data.total);
            setPage(appRes.data.page);
            setAuthItems(authRes.data.items);
            setAuthTotal(authRes.data.total);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setActorMeta(null);
        loadActorMeta();
        load(1);
        /* eslint-disable-next-line */
    }, [actorId]);

    const pages = Math.max(1, Math.ceil(total / limit));
    const displayName = actorMeta?.name || 'User';

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <Link to="/admin/activity" className="inline-flex items-center gap-1.5 text-sm text-brand-teal hover:underline mb-2">
                        <ArrowLeft className="h-4 w-4" /> Back to full activity log
                    </Link>
                    <h1 className="text-2xl font-extrabold">{displayName}&apos;s activity</h1>
                    <p className="text-sm text-ink-500">
                        {actorMeta?.role && <span className="uppercase tracking-wider text-[11px] font-semibold text-ink-400 mr-2">{actorMeta.role}</span>}
                        {actorMeta?.email && <span>{actorMeta.email} · </span>}
                        {authTotal.toLocaleString()} sign-in {authTotal === 1 ? 'event' : 'events'}
                        {' · '}
                        {total.toLocaleString()} app {total === 1 ? 'action' : 'actions'}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to={`/admin/users/${actorId}`} className="ag-btn-ghost text-sm">
                        <User className="h-4 w-4" /> View profile
                    </Link>
                    <button className="ag-btn-ghost" onClick={() => load(page)}>
                        <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Reload
                    </button>
                </div>
            </div>

            <section className="space-y-2">
                <div>
                    <h2 className="text-lg font-bold">Sign-in activity</h2>
                    <p className="text-sm text-ink-500">When this person signed in or signed out of the app.</p>
                </div>
                <AuthSessionTable items={authItems} />
                {authItems.length === 0 && actorMeta?.lastLoginAt && (
                    <p className="text-xs text-ink-500 px-1">
                        Last known sign-in: {new Date(actorMeta.lastLoginAt).toLocaleString()}
                        {' '}(detailed sign-in and sign-out history is recorded from now on.)
                    </p>
                )}
            </section>

            <section className="space-y-2">
                <div>
                    <h2 className="text-lg font-bold">App activity</h2>
                    <p className="text-sm text-ink-500">Changes they made in the system — clients, approvals, settings, and more.</p>
                </div>
                <ActivityLogTable
                    items={items}
                    showActor={false}
                    emptyMessage="No app activity recorded for this person yet."
                />
            </section>

            {pages > 1 && (
                <div className="flex items-center justify-center gap-2 text-sm">
                    <button className="ag-btn-ghost text-xs" disabled={page === 1} onClick={() => load(page - 1)}>← Prev</button>
                    <span className="text-ink-500">Page {page} of {pages}</span>
                    <button className="ag-btn-ghost text-xs" disabled={page === pages} onClick={() => load(page + 1)}>Next →</button>
                </div>
            )}
        </div>
    );
}
